import { UserInput, USERINPUTTYPE } from "./internal";
/**
 * UserInput Handler class
 *
 * @author Robert Wüest ported from Joel Burkhart, Benjamin Loesch
 * @class UserInputHandler
 * @constructor
 */
export class UserInputHandler {
    constructor(scene) {
        this._scene = null;
        this.subscribedEvents = {};
        this.keyEventsEnabled = false;
        this._draggingactive = false;
        this._mouseDownX = 0;
        this._mouseDownY = 0;
        this._scene = scene;
        for (const it in USERINPUTTYPE) {
            this.subscribedEvents[UserInput.ToString(parseInt(it))] = { eventNrIt: -1, sList: [] };
        }
        const engineInputHandler = this._scene.infrascene.userInputHandler;
        //Register the mouse- and key events from the context
        engineInputHandler.registerEvent("onKeyDown", (evt) => {
            this.handleKeyEvent(evt);
        });
        engineInputHandler.registerEvent("onKeyUp", (evt) => {
            this.handleKeyUpEvent(evt);
        });
        engineInputHandler.registerEvent("onMouseDown", (evt) => {
            this.handleMouseDownEvent(evt);
            this.handleClickEvent(evt);
        });
        engineInputHandler.registerEvent("onMouseMove", (evt) => {
            this.handleMouseMoveEvent(evt);
        });
        engineInputHandler.registerEvent("onMouseUp", (evt) => {
            this.handleMouseUpEvent(evt);
        });
        engineInputHandler.registerEvent("onDoubleClick", (evt) => {
            this.handleDblClickEvent(evt);
        });
        engineInputHandler.registerEvent("onMouseOut", (evt) => {
            this.handleMouseOutEvent(evt);
        });
        engineInputHandler.registerEvent("onMouseWheel", (evt, zoomRelative) => {
            this.handleMouseWheel(evt, zoomRelative);
        });
        this.keyEventsEnabled = true;
        this._scene.infrascene.onPanoZoom = (normalizedZoom) => {
            this.handlePanoZoom(normalizedZoom);
        };
        this._scene.infrascene.onSceneZoom = (normalizedZoom) => {
            this.handleSceneZoom(normalizedZoom);
        };
    }
    /**
     * Initialize and registers events related to the scene and the lrhandler.
     * @method initSceneEvents
     */
    initSceneEvents() {
        //Register the OnStreamChanged-Event from the scene
        this._scene.setOnGeoFrameCollectionChanged((geoframecollection) => {
            return this.handleGeoFrameCollectionChangedEvent(geoframecollection);
        });
        //Register the OnNavigationAxisChanged-Event from the LRHandler
        this._scene.getLRHandler().setOnNavigationAxisChanged((axis) => {
            return this.handleNavigationAxisChangedEvent(axis);
        });
        //Register the OnMeasurementAxisChanged-Event from the LRHandler
        this._scene.getLRHandler().setOnMeasurementAxisChanged((axis) => {
            return this.handleMeasurementAxisChangedEvent(axis);
        });
        //Register the OnLayerChanged-Event from the layerhandler
        this._scene.getLayerHandler().setOnLayerChanged((layer) => {
            return this.handleLayerChangedEvent(layer);
        });
        //Register the OnLayerAdded-Event from the layerhandler
        this._scene.getLayerHandler().setOnLayerAdded((layer) => {
            return this.handleLayerAddedEvent(layer);
        });
        //Register the OnLayerRemoved-Event from the layerhandler
        this._scene.getLayerHandler().setOnLayerRemoved((layer) => {
            return this.handleLayerRemovedEvent(layer);
        });
    }
    /**
     * subscribes an event-type in the userinputhandler.
     * @method subscribeEvent
     * @param {USERINPUTTYPE} type The mmUserInput-Type.
     * @param {function()} callbackfunction
     * @param {Object} callbackcontext
     * @return {number} The event-number of the subscribed event. Can be used to unsubscribe the event.
     */
    subscribeEvent(type, callbackfunction, callbackcontext) {
        this.subscribedEvents[UserInput.ToString(type)].eventNrIt += 1;
        this.subscribedEvents[UserInput.ToString(type)].sList.push({
            eventNr: this.subscribedEvents[UserInput.ToString(type)].eventNrIt,
            cbFunction: callbackfunction,
            cbContext: callbackcontext,
        });
        return this.subscribedEvents[UserInput.ToString(type)].eventNrIt;
    }
    /**
     * unsubscribes an event-type in the userinputhandler.
     *
     * @method unSubscribeEvent
     * @param {USERINPUTTYPE} eventType
     * @param {number} eventid The event-number
     */
    unSubscribeEvent(eventType, eventid) {
        const evtarr = this.subscribedEvents[UserInput.ToString(eventType)];
        for (let i = 0; i < evtarr.sList.length; i++) {
            if (evtarr.sList[i].eventNr == eventid) {
                evtarr.sList.splice(i, 1);
                i--;
            }
        }
    }
    /**
     * Released when a geoframecollection has changed. Registers new events on the new stream.
     * Release all subscribed streamchanged-events.
     * @method handleGeoFrameCollectionChangedEvent
     * @param {Stream} geoframecollection the current stream
     */
    handleGeoFrameCollectionChangedEvent(geoframecollection) {
        geoframecollection.setOnGeoFrameChanged((geoframe) => {
            return this.handleGeoFrameChangedEvent(geoframe);
        });
        geoframecollection.setOnBuffering((on) => {
            return this.handleBufferingEvent(on);
        });
        geoframecollection.setOnTileLoading((on) => {
            return this.handleTileLoadingEvent(on);
        });
        geoframecollection.setPanoRotationEvent((lat, lng, fov) => {
            return this.handlePanoRotation(lat, lng, fov);
        });
        for (let i = 0; i < this.subscribedEvents["GEOFRAMECOLLECTIONCHANGED"].sList.length; i++) {
            this.subscribedEvents["GEOFRAMECOLLECTIONCHANGED"].sList[i].cbFunction.call(this.subscribedEvents["GEOFRAMECOLLECTIONCHANGED"].sList[i].cbContext, geoframecollection);
        }
    }
    /**
     * Releases all subscribed key-events
     * @method handleKeyEvent
     * @param evt
     */
    handleKeyEvent(evt) {
        if (!this.keyEventsEnabled)
            return;
        switch (evt.keyCode) {
            case 32:
                //space
                for (let i = 0; i < this.subscribedEvents["KEY_SPACE"].sList.length; i++) {
                    this.subscribedEvents["KEY_SPACE"].sList[i].cbFunction.call(this.subscribedEvents["KEY_SPACE"].sList[i].cbContext, evt);
                }
                break;
            case 27:
                //space
                for (let i = 0; i < this.subscribedEvents["KEY_ESC"].sList.length; i++) {
                    this.subscribedEvents["KEY_ESC"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ESC"].sList[i].cbContext, evt);
                }
                break;
            case 13:
                //enter
                for (let i = 0; i < this.subscribedEvents["KEY_ENTER"].sList.length; i++) {
                    this.subscribedEvents["KEY_ENTER"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ENTER"].sList[i].cbContext, evt);
                }
                break;
            case 8:
                //backspace
                for (let i = 0; i < this.subscribedEvents["KEY_BACKSPACE"].sList.length; i++) {
                    this.subscribedEvents["KEY_BACKSPACE"].sList[i].cbFunction.call(this.subscribedEvents["KEY_BACKSPACE"].sList[i].cbContext, evt);
                }
                break;
            case 9:
                //tab
                for (let i = 0; i < this.subscribedEvents["KEY_TAB"].sList.length; i++) {
                    this.subscribedEvents["KEY_TAB"].sList[i].cbFunction.call(this.subscribedEvents["KEY_TAB"].sList[i].cbContext, evt);
                }
                break;
            case 46:
                //delete
                for (let i = 0; i < this.subscribedEvents["KEY_DELETE"].sList.length; i++) {
                    this.subscribedEvents["KEY_DELETE"].sList[i].cbFunction.call(this.subscribedEvents["KEY_DELETE"].sList[i].cbContext, evt);
                }
                break;
            case 16:
                if (evt.ctrlKey) {
                    //shift & control
                    for (let i = 0; i < this.subscribedEvents["KEY_CTRL_SHIFT"].sList.length; i++) {
                        this.subscribedEvents["KEY_CTRL_SHIFT"].sList[i].cbFunction.call(this.subscribedEvents["KEY_CTRL_SHIFT"].sList[i].cbContext, evt);
                    }
                }
                else {
                    //shift without control
                    for (let i = 0; i < this.subscribedEvents["KEY_SHIFT"].sList.length; i++) {
                        this.subscribedEvents["KEY_SHIFT"].sList[i].cbFunction.call(this.subscribedEvents["KEY_SHIFT"].sList[i].cbContext, evt);
                    }
                }
                break;
            case 17:
                //ctrl
                for (let i = 0; i < this.subscribedEvents["KEY_CTRL"].sList.length; i++) {
                    this.subscribedEvents["KEY_CTRL"].sList[i].cbFunction.call(this.subscribedEvents["KEY_CTRL"].sList[i].cbContext, evt);
                }
                break;
            //AltGr is Alt+Ctrl but we probably
            case 18:
                //alt
                for (let i = 0; i < this.subscribedEvents["KEY_ALT"].sList.length; i++) {
                    this.subscribedEvents["KEY_ALT"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ALT"].sList[i].cbContext, evt);
                }
                this._scene.infrascene.render();
                break;
            case 39:
                //right
                for (let i = 0; i < this.subscribedEvents["KEY_ARROW_RIGHT"].sList.length; i++) {
                    this.subscribedEvents["KEY_ARROW_RIGHT"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ARROW_RIGHT"].sList[i].cbContext, evt);
                }
                break;
            case 37:
                //left
                for (let i = 0; i < this.subscribedEvents["KEY_ARROW_LEFT"].sList.length; i++) {
                    this.subscribedEvents["KEY_ARROW_LEFT"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ARROW_LEFT"].sList[i].cbContext, evt);
                }
                break;
            case 38:
                //up
                for (let i = 0; i < this.subscribedEvents["KEY_ARROW_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_ARROW_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ARROW_UP"].sList[i].cbContext, evt);
                }
                break;
            case 40:
                //right
                for (let i = 0; i < this.subscribedEvents["KEY_ARROW_DOWN"].sList.length; i++) {
                    this.subscribedEvents["KEY_ARROW_DOWN"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ARROW_DOWN"].sList[i].cbContext, evt);
                }
                break;
            case 67:
                //c
                for (let i = 0; i < this.subscribedEvents["KEY_C"].sList.length; i++) {
                    this.subscribedEvents["KEY_C"].sList[i].cbFunction.call(this.subscribedEvents["KEY_C"].sList[i].cbContext, evt);
                }
                break;
            case 65:
                //a
                for (let i = 0; i < this.subscribedEvents["KEY_A"].sList.length; i++) {
                    this.subscribedEvents["KEY_A"].sList[i].cbFunction.call(this.subscribedEvents["KEY_A"].sList[i].cbContext, evt);
                }
                break;
            case 68:
                //d
                for (let i = 0; i < this.subscribedEvents["KEY_D"].sList.length; i++) {
                    this.subscribedEvents["KEY_D"].sList[i].cbFunction.call(this.subscribedEvents["KEY_D"].sList[i].cbContext, evt);
                }
                break;
        }
    }
    /**
     * Releases all subscribed key-events
     * @method handleKeyUpEvent
     * @param evt
     */
    handleKeyUpEvent(evt) {
        if (!this.keyEventsEnabled)
            return;
        switch (evt.keyCode) {
            case 32:
                //space
                for (let i = 0; i < this.subscribedEvents["KEY_SPACE_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_SPACE_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_SPACE_UP"].sList[i].cbContext, evt);
                }
                break;
            case 27:
                //space
                for (let i = 0; i < this.subscribedEvents["KEY_ESC_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_ESC_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ESC_UP"].sList[i].cbContext, evt);
                }
                break;
            case 13:
                //enter
                for (let i = 0; i < this.subscribedEvents["KEY_ENTER_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_ENTER_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ENTER_UP"].sList[i].cbContext, evt);
                }
                break;
            case 8:
                //backspace
                for (let i = 0; i < this.subscribedEvents["KEY_BACKSPACE_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_BACKSPACE_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_BACKSPACE_UP"].sList[i].cbContext, evt);
                }
                break;
            case 9:
                //tab
                for (let i = 0; i < this.subscribedEvents["KEY_TAB_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_TAB_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_TAB_UP"].sList[i].cbContext, evt);
                }
                break;
            case 46:
                //delete
                for (let i = 0; i < this.subscribedEvents["KEY_DELETE_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_DELETE_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_DELETE_UP"].sList[i].cbContext, evt);
                }
                break;
            case 17:
                //ctrl
                for (let i = 0; i < this.subscribedEvents["KEY_CTRL_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_CTRL_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_CTRL_UP"].sList[i].cbContext, evt);
                }
                break;
            case 18:
                //alt
                for (let i = 0; i < this.subscribedEvents["KEY_ALT_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_ALT_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ALT_UP"].sList[i].cbContext, evt);
                }
                break;
            case 39:
                //right
                for (let i = 0; i < this.subscribedEvents["KEY_ARROW_RIGHT_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_ARROW_RIGHT_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ARROW_RIGHT_UP"].sList[i].cbContext, evt);
                }
                break;
            case 37:
                //left
                for (let i = 0; i < this.subscribedEvents["KEY_ARROW_LEFT_UP"].sList.length; i++) {
                    this.subscribedEvents["KEY_ARROW_LEFT_UP"].sList[i].cbFunction.call(this.subscribedEvents["KEY_ARROW_LEFT_UP"].sList[i].cbContext, evt);
                }
                break;
        }
    }
    /**
     * Disable fireing of key events
     * @method disableKeyEvents
     */
    disableKeyEvents() {
        this.keyEventsEnabled = false;
    }
    /**
     * Enable fireing of key events
     * @method enableKeyEvents
     */
    enableKeyEvents() {
        this.keyEventsEnabled = true;
    }
    /**
     * releases all subscribed click-events
     * @method handleClickEvent
     * @param evt
     */
    handleClickEvent(evt) {
        if (evt.button == 0) {
            //left mouse click
            const leftclicks = this.subscribedEvents["LEFTCLICK"].sList.slice();
            for (let i = 0; i < leftclicks.length; i++) {
                leftclicks[i].cbFunction.call(leftclicks[i].cbContext, evt);
            }
        }
        else if (evt.button == 1) {
            //middle click
            const middleclicks = this.subscribedEvents["MIDDLECLICK"].sList.slice();
            for (let i = 0; i < middleclicks.length; i++) {
                middleclicks[i].cbFunction.call(middleclicks[i].cbContext, evt);
            }
        }
        else if (evt.button == 2) {
            //right click
            const rightclicks = this.subscribedEvents["RIGHTCLICK"].sList.slice();
            for (let i = 0; i < rightclicks.length; i++) {
                rightclicks[i].cbFunction.call(rightclicks[i].cbContext, evt);
            }
        }
    }
    /**
     * releases all subscribed mouse-move-events.
     * @method handleMouseMoveEvent
     * @param evt
     */
    handleMouseMoveEvent(evt) {
        this._draggingactive = evt.isDragging;
        for (let i = 0; i < this.subscribedEvents["MOUSEMOVE"].sList.length; i++) {
            this.subscribedEvents["MOUSEMOVE"].sList[i].cbFunction.call(this.subscribedEvents["MOUSEMOVE"].sList[i].cbContext, evt);
        }
    }
    /**
     * releases all subscribed mouse-down-events
     * @method  handleMouseDownEvent
     * @param evt
     */
    handleMouseDownEvent(evt) {
        this._mouseDownX = evt.clientX;
        this._mouseDownY = evt.clientY;
        this._draggingactive = false;
        for (let i = 0; i < this.subscribedEvents["MOUSEDOWN"].sList.length; i++) {
            this.subscribedEvents["MOUSEDOWN"].sList[i].cbFunction.call(this.subscribedEvents["MOUSEDOWN"].sList[i].cbContext, evt);
        }
    }
    /**
     * releases all subscribed mouse-up-events
     * @method
     * @param evt
     */
    handleMouseUpEvent(evt) {
        evt.isDragging = this._draggingactive;
        for (let i = this.subscribedEvents["MOUSEUP"].sList.length - 1; i >= 0; i--) {
            this.subscribedEvents["MOUSEUP"].sList[i].cbFunction.call(this.subscribedEvents["MOUSEUP"].sList[i].cbContext, evt);
        }
    }
    /**
     * releases all subscribed mouse-out-events.
     * @method handleMouseOutEvent
     * @param evt
     */
    handleMouseOutEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["MOUSEOUT"].sList.length; i++) {
            this.subscribedEvents["MOUSEOUT"].sList[i].cbFunction.call(this.subscribedEvents["MOUSEOUT"].sList[i].cbContext, evt);
        }
    }
    /**
     * releases all subscribed double-click-events
     * @method handleDblClickEvent
     * @param evt
     */
    handleDblClickEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["DOUBLECLICK"].sList.length; i++) {
            this.subscribedEvents["DOUBLECLICK"].sList[i].cbFunction.call(this.subscribedEvents["DOUBLECLICK"].sList[i].cbContext, evt);
        }
    }
    /**
     * releases all subscribed imagechanged-events
     * @method handleGeoFrameChangedEvent
     * @param {string} activeimage
     */
    handleImageChangedEvent(activeimage) {
        for (let i = 0; i < this.subscribedEvents["IMAGECHANGED"].sList.length; i++) {
            this.subscribedEvents["IMAGECHANGED"].sList[i].cbFunction.call(this.subscribedEvents["IMAGECHANGED"].sList[i].cbContext, activeimage);
        }
    }
    /**
     * releases all subscribed featureselected-events
     * @method handleFeatureSelectedEvent
     * @param {Layer} layer
     * @param {string} id - Feature ID
     */
    handleFeatureSelectedEvent(layer, id) {
        for (let i = 0; i < this.subscribedEvents["FEATURESELECTED"].sList.length; i++) {
            this.subscribedEvents["FEATURESELECTED"].sList[i].cbFunction.call(this.subscribedEvents["FEATURESELECTED"].sList[i].cbContext, layer, id);
        }
    }
    /**
     * releases all subscribed featuredeselected-events
     * @method handleFeatureDeSelectedEvent
     * @param {Layer} layer
     * @param {number} id - Feature ID
     */
    handleFeatureDeSelectedEvent(layer, id) {
        for (let i = 0; i < this.subscribedEvents["FEATUREDESELECTED"].sList.length; i++) {
            this.subscribedEvents["FEATUREDESELECTED"].sList[i].cbFunction.call(this.subscribedEvents["FEATUREDESELECTED"].sList[i].cbContext, layer, id);
        }
    }
    /**
     * releases all subscribed geoframechanged-events
     * @method handleGeoFrameChangedEvent
     * @param {GeoFrame} geoframe
     */
    handleGeoFrameChangedEvent(geoframe) {
        for (let i = 0; i < this.subscribedEvents["GEOFRAMECHANGED"].sList.length; i++) {
            this.subscribedEvents["GEOFRAMECHANGED"].sList[i].cbFunction.call(this.subscribedEvents["GEOFRAMECHANGED"].sList[i].cbContext, geoframe);
        }
    }
    /**
     * releases all subscribed streamstarted-events
     * @method handleStreamStartedEvent
     */
    handleStreamStartedEvent() {
        for (let i = 0; i < this.subscribedEvents["STREAMSTARTED"].sList.length; i++) {
            this.subscribedEvents["STREAMSTARTED"].sList[i].cbFunction.call(this.subscribedEvents["STREAMSTARTED"].sList[i].cbContext);
        }
    }
    /**
     * releases all subscribed streamstopped-events
     * @method handleStreamStoppedEvent
     */
    handleStreamStoppedEvent() {
        for (let i = 0; i < this.subscribedEvents["STREAMSTOPPED"].sList.length; i++) {
            this.subscribedEvents["STREAMSTOPPED"].sList[i].cbFunction.call(this.subscribedEvents["STREAMSTOPPED"].sList[i].cbContext);
        }
    }
    /**
     * releases all subscribed buffering-events
     * @method handleBufferingEvent
     * @param {boolean} on
     */
    handleBufferingEvent(on) {
        for (let i = 0; i < this.subscribedEvents["BUFFERING"].sList.length; i++) {
            this.subscribedEvents["BUFFERING"].sList[i].cbFunction.call(this.subscribedEvents["BUFFERING"].sList[i].cbContext, on);
        }
    }
    /**
     * releases all subscribed buffering-events
     * @method handleTileLoadingEvent
     * @param {boolean} on
     */
    handleTileLoadingEvent(on) {
        for (let i = 0; i < this.subscribedEvents["TILE_LOADING"].sList.length; i++) {
            this.subscribedEvents["TILE_LOADING"].sList[i].cbFunction.call(this.subscribedEvents["TILE_LOADING"].sList[i].cbContext, on);
        }
    }
    /**
     * releases all subscribed navigationaxischanged-events
     * @method handleNavigationAxisChangedEvent
     * @param {Axis} axis
     */
    handleNavigationAxisChangedEvent(axis) {
        for (let i = 0; i < this.subscribedEvents["NAVIGATION_AXISCHANGED"].sList.length; i++) {
            this.subscribedEvents["NAVIGATION_AXISCHANGED"].sList[i].cbFunction.call(this.subscribedEvents["NAVIGATION_AXISCHANGED"].sList[i].cbContext, axis);
        }
    }
    /**
     * releases all subscribed measurementaxischanged-events
     * @method handleMeasurementAxisChangedEvent
     * @param {Axis} axis
     */
    handleMeasurementAxisChangedEvent(axis) {
        for (let i = 0; i < this.subscribedEvents["MEASUREMENT_AXISCHANGED"].sList.length; i++) {
            this.subscribedEvents["MEASUREMENT_AXISCHANGED"].sList[i].cbFunction.call(this.subscribedEvents["MEASUREMENT_AXISCHANGED"].sList[i].cbContext, axis);
        }
    }
    /**
     * releases all subscribed layerchanged-events
     * @method handleLayerChangedEvent
     * @param {Layer} layer
     */
    handleLayerChangedEvent(layer) {
        for (let i = 0; i < this.subscribedEvents["LAYERCHANGED"].sList.length; i++) {
            this.subscribedEvents["LAYERCHANGED"].sList[i].cbFunction.call(this.subscribedEvents["LAYERCHANGED"].sList[i].cbContext, layer);
        }
    }
    /**
     * releases all subscribed layeradded-events
     * @method handleLayeraddedEvent
     * @param {Layer} layer
     */
    handleLayerAddedEvent(layer) {
        layer.setOnLayerUpdated((layer, updates) => {
            return this.handleLayerUpdatedEvent(layer, updates);
        });
        layer.setOnLayerSaved((layer, inserts, updates, deletions) => {
            return this.handleLayerSavedEvent(layer, inserts, updates, deletions);
        });
        for (let i = 0; i < this.subscribedEvents["LAYERADDED"].sList.length; i++) {
            this.subscribedEvents["LAYERADDED"].sList[i].cbFunction.call(this.subscribedEvents["LAYERADDED"].sList[i].cbContext, layer);
        }
    }
    /**
     * releases all subscribed layerupdated-events
     * @method handleLayerUpdatedEvent
     * @param {Layer} layer
     * @param {Layer} updates
     */
    handleLayerUpdatedEvent(layer, updates) {
        for (let i = 0; i < this.subscribedEvents["LAYERUPDATED"].sList.length; i++) {
            this.subscribedEvents["LAYERUPDATED"].sList[i].cbFunction.call(this.subscribedEvents["LAYERUPDATED"].sList[i].cbContext, layer, updates);
        }
    }
    /**
     * releases all subscribed layerupdated-events
     * @method handleLayerSavedEvent
     * @param layer
     * @param inserts
     * @param updates
     * @param deletions
     */
    handleLayerSavedEvent(layer, inserts, updates, deletions) {
        for (let i = 0; i < this.subscribedEvents["LAYERSAVED"].sList.length; i++) {
            this.subscribedEvents["LAYERSAVED"].sList[i].cbFunction.call(this.subscribedEvents["LAYERSAVED"].sList[i].cbContext, layer, inserts, updates, deletions);
        }
    }
    /**
     * releases all subscribed layerremoved-events
     * @method handleLayerRemovedEvent
     * @param {Layer} layer
     */
    handleLayerRemovedEvent(layer) {
        for (let i = 0; i < this.subscribedEvents["LAYERREMOVED"].sList.length; i++) {
            this.subscribedEvents["LAYERREMOVED"].sList[i].cbFunction.call(this.subscribedEvents["LAYERREMOVED"].sList[i].cbContext, layer);
        }
    }
    /**
     * releases all subscribed mousewheel-events
     * @method handleMouseWheel
     * @param evt
     * @param zoomRelative
     */
    handleMouseWheel(evt, zoomRelative) {
        for (let i = 0; i < this.subscribedEvents["MOUSEWHEEL"].sList.length; i++) {
            this.subscribedEvents["MOUSEWHEEL"].sList[i].cbFunction.call(this.subscribedEvents["MOUSEWHEEL"].sList[i].cbContext, evt, zoomRelative);
        }
    }
    /**
     * Handles the touch event 'tap'
     * @method handleTapEvent
     * @param evt
     */
    handleTapEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["TAP"].sList.length; i++) {
            this.subscribedEvents["TAP"].sList[i].cbFunction.call(this.subscribedEvents["TAP"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'hold'
     * @method handleHoldEvent
     * @param evt
     */
    handleHoldEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["HOLD"].sList.length; i++) {
            this.subscribedEvents["HOLD"].sList[i].cbFunction.call(this.subscribedEvents["HOLD"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'drag'
     * @method handleDragEvent
     * @param evt
     */
    handleDragEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["DRAG"].sList.length; i++) {
            this.subscribedEvents["DRAG"].sList[i].cbFunction.call(this.subscribedEvents["DRAG"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'dragstart'
     * @method handleDragstartEvent
     * @param evt
     */
    handleDragstartEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["DRAGSTART"].sList.length; i++) {
            this.subscribedEvents["DRAGSTART"].sList[i].cbFunction.call(this.subscribedEvents["DRAGSTART"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'dragend'
     * @method handleDragendEvent
     * @param evt
     */
    handleDragendEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["DRAGEND"].sList.length; i++) {
            this.subscribedEvents["DRAGEND"].sList[i].cbFunction.call(this.subscribedEvents["DRAGEND"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'pinch'
     * @method handlePinchEvent
     * @param evt
     */
    handlePinchEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["PINCH"].sList.length; i++) {
            this.subscribedEvents["PINCH"].sList[i].cbFunction.call(this.subscribedEvents["PINCH"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'touch'.
     * @method handleTouchEvent
     * @param evt
     */
    handleTouchEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["TOUCH"].sList.length; i++) {
            this.subscribedEvents["TOUCH"].sList[i].cbFunction.call(this.subscribedEvents["TOUCH"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'release'
     * @method handleReleaseEvent
     * @param evt
     */
    handleReleaseEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["RELEASE"].sList.length; i++) {
            this.subscribedEvents["RELEASE"].sList[i].cbFunction.call(this.subscribedEvents["RELEASE"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'doubletap'
     * @method handleDoubletapEvent
     * @param evt
     */
    handleDoubletapEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["DOUBLETAP"].sList.length; i++) {
            this.subscribedEvents["DOUBLETAP"].sList[i].cbFunction.call(this.subscribedEvents["DOUBLETAP"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the touch event 'swipe'
     * @method handleSwipeEvent
     * @param evt
     */
    handleSwipeEvent(evt) {
        for (let i = 0; i < this.subscribedEvents["SWIPE"].sList.length; i++) {
            this.subscribedEvents["SWIPE"].sList[i].cbFunction.call(this.subscribedEvents["SWIPE"].sList[i].cbContext, evt);
        }
    }
    /**
     * Handles the panorama-sphere rotation event
     * @method handlePanoRotation
     */
    handlePanoRotation(lat, lng, fov) {
        for (let i = 0; i < this.subscribedEvents["PANO_ROTATION"].sList.length; i++) {
            this.subscribedEvents["PANO_ROTATION"].sList[i].cbFunction.call(this.subscribedEvents["PANO_ROTATION"].sList[i].cbContext, lat, lng, fov);
        }
    }
    /**
     * Handles the panorama-sphere zoom event
     * @method handlePanoZoom
     */
    handlePanoZoom(normalizedZoom) {
        for (let i = 0; i < this.subscribedEvents["PANO_ZOOM"].sList.length; i++) {
            this.subscribedEvents["PANO_ZOOM"].sList[i].cbFunction.call(this.subscribedEvents["PANO_ZOOM"].sList[i].cbContext, normalizedZoom);
        }
    }
    /**
     * Handles the panorama-sphere zoom event
     * @method handlePanoZoom
     */
    handleSceneZoom(normalizedZoom) {
        for (let i = 0; i < this.subscribedEvents["SCENE_ZOOM"].sList.length; i++) {
            this.subscribedEvents["SCENE_ZOOM"].sList[i].cbFunction.call(this.subscribedEvents["SCENE_ZOOM"].sList[i].cbContext, normalizedZoom);
        }
    }
    handlePlayModeChanged(playMode) {
        for (let i = 0; i < this.subscribedEvents["PLAYMODE_CHANGED"].sList.length; i++) {
            this.subscribedEvents["PLAYMODE_CHANGED"].sList[i].cbFunction.call(this.subscribedEvents["PLAYMODE_CHANGED"].sList[i].cbContext, playMode);
        }
    }
    handleMeasureModeChangedEvent(measureMode) {
        for (let i = 0; i < this.subscribedEvents["MEASUREMODE_CHANGED"].sList.length; i++) {
            this.subscribedEvents["MEASUREMODE_CHANGED"].sList[i].cbFunction.call(this.subscribedEvents["MEASUREMODE_CHANGED"].sList[i].cbContext, measureMode);
        }
    }
}
//# sourceMappingURL=userinputhandler.js.map